import * as React from "react";
import * as Storage from "aws-amplify/storage";
import {
  Formik,
  FormikActions
} from "formik";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { $enum } from "ts-enum-util";
import * as yup from "yup";

import useCutoffByDate from "src/core/orders/hooks/validation/useCutoffByDate";
import { completeLeadTimeConfiguration } from "src/core/orders/utils/order-deliveries";
import {
  IProductStatus,
  ProductStatus,
  ShelfLifePeriod
} from "src/core/products/api/products.api";
import ProductInfoSidebar from "src/core/products/components/ProductInfoSidebar";
import ProductStatuses from "src/core/products/components/ProductStatuses";
import { ProductStatusRowType } from "src/core/products/components/ProductStatuses/FutureProductStatuses";
import { useProduct } from "src/core/products/hooks";
import { useProductLeadTimeGroup } from "src/core/products/hooks/useProductLeadTimeGroup";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { useInheritedSettingsApi } from "src/core/shared/api/inheritedSettings.api";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import type { IStore } from "src/core/shared/store";
import { useSuppliersApi } from "src/core/suppliers/api/suppliers.api";
import { useCurrentUser } from "src/core/users/hooks";
import { Button } from "src/library/components/Button";
import {
  FormRow,
  Input,
  InputImage,
  Label,
  Select,
  TextArea
} from "src/library/components/Forms";
import { Switch } from "src/library/components/Forms/Switch";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { ImageCroppingModal } from "src/library/components/Modal";
import {
  IProductTag,
  ITag
} from "src/modules/product-tags/api/product-tags.api";
import ProductTagSelector from "src/modules/product-tags/components/ProductTagSelector";
import { PageProps } from "src/pages/Router/routes";
import {
  ERPRJL,
  ERPSeedless
} from "src/utils/constants";

interface ProductDetailsPageProps {
  productId: string;
}

interface ProductFormValues {
  leadTimeGroupId: string;
  description: string;
  infoKey?: string;
  ingredients?: string;
  madeToOrder: boolean;
  creditOnly: boolean;
  partNumber: string | null;
  productImage: File | null;
  nutritionImage: File | null;
  published: string;
  currentStatus?: IProductStatus;
  productStatuses?: IProductStatus[];
  tags: IProductTag[];
  barcode?: string;
  shelfLife?: number | null;
  shelfLifePeriod?: ShelfLifePeriod | null;
  minimumQuantity?: number | null;
  weight?: number | null;
  supplierId?: string | null;
  supplierPartNumber?: string | null
}

interface ImgCropperModalState {
  type: "productImage" | "nutritionImage";
  file: File;
}

const published = [ "Not published", "Published" ];

const productSchema = yup.object().shape({
  productImage: yup.mixed(),
  nutritionImage: yup.mixed(),
  description: yup
    .string()
    .min(1)
    .required("Required"),
  madeToOrder: yup
    .boolean()
    .required()
    .nullable(),
  leadTimeGroupId: yup.number().required("Required"),
  productStatuses: yup.array(),
  published: yup.string().required(),
  creditOnly: yup
    .boolean()
    .required()
    .nullable(),
  ingredients: yup.string().nullable(),
  partNumber: yup
    .string()
    .min(1)
    .required("Required"),
  tags: yup.array(),
  barcode: yup.string().nullable(),
  shelfLife: yup.number().nullable(),
  shelfLifePeriod: yup.string().nullable(),
  minimumQuantity: yup.number().nullable(),
  weight: yup.number().nullable(),
  supplierPartNumber: yup.string().nullable()
});

const ProductDetailsPage: React.ReactFCWithChildren<PageProps<ProductDetailsPageProps, {productId: string}>> = props => {
  const {
    match, history, terminology
  } = props;

  const productId = match ? parseInt(match.params.productId, 0) : 0;

  const {
    isLoading: isLoadingProduct, product, isSavingProduct, saveProduct
  } = useProduct(productId);

  const [ imgCropperModal, setImgCropperModal ] = React.useState<ImgCropperModalState>();
  const [ suppliersRes, suppliersReq ] = useSuppliersApi("LIST_SUPPLIERS");
  const [ productImportSettingRes, productImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");
  const tagRef = React.useRef<{ currentTags: ITag[] }>(null);
  const productStatusesRef = React.useRef<{currentFutureStatuses: ProductStatusRowType[]}>(null);
  const formRef = React.useRef<Formik<ProductFormValues>>(null);
  const [ { currentRole } ] = useCurrentUser();
  const getProductLeadTime = useProductLeadTimeGroup();
  const [ { globalSettings } ] = useGlobalSettings();
  const [ globalDeliveryWeekLeadTimesRes, globalDeliveryWeekLeadTimesReq ] = useInheritedSettingsApi("GET_GLOBAL_SETTING");
  const [ globalDefaultCutoffTimeRes, globalDefaultCutoffTimeReq ] = useInheritedSettingsApi("GET_GLOBAL_SETTING");
  const cutoffByDate = useCutoffByDate();

  const { erp, installedModules } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings ? global.settings.installedModules : []
  }));

  const disabled = erp !== ERPSeedless && erp !== ERPRJL;
  const isLoading = React.useMemo(() => isLoadingProduct || suppliersRes.pending, [ isLoadingProduct, suppliersRes.pending ]);

  React.useEffect(() => {
    productImportSettingReq({
      type: ImportSettingType.Product,
      origin: ImportOrigin.CSV
    });
  }, [ productImportSettingReq ]);

  const supplierHeaderMapping = React.useMemo(() => {
    return productImportSettingRes?.data?.headerMappings.find(headerMapping => headerMapping.internal === "SupplierSlug");
  }, [ productImportSettingRes ]);

  // Fetch list of suppliers on mount
  React.useEffect(() => {
    suppliersReq();
  }, [ suppliersReq ]);

  // Fetch global delivery week lead times setting on mount
  React.useEffect(() => {
    globalDeliveryWeekLeadTimesReq({ name: "delivery_week_leadtimes" });
  }, [ globalDeliveryWeekLeadTimesReq ]);

  // Fetch global delivery cutoff setting on mount
  React.useEffect(() => {
    globalDefaultCutoffTimeReq({ name: "delivery_cutoff" });
  }, [ globalDefaultCutoffTimeReq ]);

  /**
   * Handle save click functionality - Submit the form
   */
  const handleSaveClick = React.useCallback(() => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  }, []);

  /**
   * Handle form submission
   */
  const handleFormSubmission = React.useCallback(
    async (values: ProductFormValues, actions: FormikActions<ProductFormValues>): Promise<void> => {
      const imageDir = "product-images";
      let image = product ? product.image : "";
      let nutritionImage = product ? product.nutritionImage : "";

      // Make sure that the image is a file before continuing
      if (values.productImage && values.productImage instanceof File) {
        try {
          const result = await Storage.uploadData({
            path: `public/${imageDir}/main/${values.productImage.name}`,
            data: values.productImage,
            options: { contentType: values.productImage.type }
          }).result;

          image = result.path.replace("public/", "");
        } catch (error) {
          actions.setError("There was an error uploading your image");

          throw error;
        }
      }

      // Make sure that the image is a file before continuing
      if (values.nutritionImage && values.nutritionImage instanceof File) {
        try {
          const result = await Storage.uploadData({
            path: `public/${imageDir}/nutrition/${values.nutritionImage.name}`,
            data: values.nutritionImage,
            options: { contentType: values.nutritionImage.type }
          }).result;

          nutritionImage = result.path.replace("public/", "");
        } catch (error) {
          actions.setError("There was an error uploading your image");

          throw error;
        }
      }

      // loop through future product statuses, ensure all start dates are after the cutoff time
      const currentFutureStatuses = productStatusesRef?.current?.currentFutureStatuses;

      if (currentFutureStatuses && product) {
        const productLeadTime = getProductLeadTime(product);

        for (const status of currentFutureStatuses) {
          if (status.id) {
            // status already exists so skip validation
            continue;
          }

          if (status.status === ProductStatus?.Active) {
            toast.error("Active future status cannot be set, as product is currently in an active state");

            return;
          }

          const statusStartDate = moment.utc(status.startDate);

          if (productLeadTime) {
            const cutoffTime = cutoffByDate(status.startDate as Date, "shortest", productLeadTime, { leadTimesOverride: completeLeadTimeConfiguration([ productLeadTime ], globalDeliveryWeekLeadTimesRes.data?.value, globalDefaultCutoffTimeRes.data?.value) });

            if (moment.utc().isAfter(cutoffTime)) {
              toast.error(`The cutoff time for the ${statusStartDate.format("DD/MM/YYYY")} has already passed. please choose a later date.`);

              return;
            }
          }
        }
      }

      const tranformedFutureStatuses = productStatusesRef.current ? productStatusesRef.current.currentFutureStatuses.map(status => {
        delete status.localId;

        return {
          ...status,
          startDate: moment.utc(status.startDate).format("YYYY-MM-DD HH:mm"),
          endDate: status.endDate ? moment.utc(status.endDate).format("YYYY-MM-DD HH:mm") : status.endDate
        };
      }) : [];

      if (product) {
        saveProduct({
          id: productId,
          description: values.description,
          image,
          nutritionImage,
          published: values.published === "published",
          leadTimeGroupId: parseInt(values.leadTimeGroupId, 10),
          ingredients: values.ingredients,
          infoKey: values.infoKey,
          madeToOrder: values.madeToOrder,
          creditOnly: values.creditOnly,
          tags: tagRef.current ? tagRef.current.currentTags : [],
          barcode: values.barcode,
          productStatuses: tranformedFutureStatuses,
          shelfLife: typeof values.shelfLife !== "number" ? null : values.shelfLife,
          shelfLifePeriod: values.shelfLifePeriod || null,
          minimumQuantity: typeof values.minimumQuantity !== "number" ? null : values.minimumQuantity,
          weight: values.weight,
          supplierId: values.supplierId ? parseInt(values.supplierId, 10) : null,
          supplierPartNumber: values.supplierPartNumber
        });
      }
    },
    [
      cutoffByDate,
      getProductLeadTime,
      globalDefaultCutoffTimeRes.data?.value,
      globalDeliveryWeekLeadTimesRes.data?.value,
      product,
      productId,
      saveProduct
    ]
  );

  // Handles the image modal dismiss functionality
  const handleImgModalOnDismiss = React.useCallback(() => {
    // Update the imgCropperModal state
    setImgCropperModal(undefined);
  }, []);

  // Handles the image modal submission functionality
  const handleImgModalOnSubmit = React.useCallback(
    (file: File) => {
      // Only if imgCropperModal and formRef.current is set
      if (imgCropperModal && formRef.current) {
        // Update the imgCropperModal state
        setImgCropperModal(undefined);
        // Set the file input field value
        formRef.current.setFieldValue(imgCropperModal.type, file);
      }
    },
    [ imgCropperModal, formRef.current ]
  );

  // On product
  React.useEffect(() => {
    if (product && formRef.current) {
      if (product.image) {
        if (formRef.current.getFormikBag().values.productImage === null) {
          formRef.current.setFieldValue("productImage", product.image);
        }
      }

      if (product.nutritionImage) {
        if (formRef.current.getFormikBag().values.nutritionImage === null) {
          formRef.current.setFieldValue("nutritionImage", product.nutritionImage);
        }
      }
    }
  }, [ product, formRef.current ]);

  return (
    <Layout
      {...props}
      title={product ? `${product.description} - Details` : ""}
    >
      <MainContentWrapper fill>
        {isLoading ? (
          <LoadingSpinner
            type="circular"
            position="inline"
          />
        ) : (
          product && (
            <>
              <Formik
                ref={formRef}
                initialValues={{
                  leadTimeGroupId: product.leadTimeGroupId.toString(),
                  description: product.description,
                  infoKey: product.infoKey,
                  ingredients: product.ingredients,
                  madeToOrder: product.madeToOrder,
                  creditOnly: product.creditOnly,
                  partNumber: product.partNumber,
                  productImage: null,
                  published: product.published ? "published" : "not published",
                  tags: [],
                  productStatuses: [],
                  nutritionImage: null,
                  barcode: product.barcode,
                  shelfLife: product.shelfLife,
                  minimumQuantity: product.minimumQuantity,
                  weight: product.weight,
                  supplierId: product.supplierId ? product.supplierId.toString() : "",
                  supplierPartNumber: product.supplierPartNumber
                }}
                validationSchema={productSchema}
                onSubmit={handleFormSubmission}
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({
                  values, handleSubmit, handleChange, setFieldValue, errors
                }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <FormSection>
                        <h2>
                          Key Information
                        </h2>

                        <Grid.Bounds style={{ flexWrap: "wrap" }}>
                          <Grid.Box style={{ paddingRight: "20px" }}>
                            <InputImage
                              label={`${terminology.product.singular.text} image`}
                              autoHideLabels
                              name="productImage"
                              handleChange={evt => {
                                setImgCropperModal({
                                  type: "productImage",
                                  file: evt.currentTarget.files[ 0 ]
                                });
                              }}
                              value={values.productImage}
                            />
                          </Grid.Box>

                          <Grid.Box>
                            <FormRow>
                              <Input
                                disabled={disabled}
                                value={values.description}
                                error={errors.description}
                                label={`${terminology.product.singular.text} name`}
                                name="description"
                                handleChange={handleChange}
                              />
                            </FormRow>
                          </Grid.Box>
                        </Grid.Bounds>

                        <FormRow>
                          <Grid.Bounds direction="horizontal">
                            <Input
                              disabled={disabled}
                              value={values.partNumber || ""}
                              label={terminology?.partNumber.singular.text || "Part number"}
                              name="partNumber"
                              handleChange={handleChange}
                              error={errors.partNumber}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds direction="horizontal">
                            <Input
                              value={values.barcode || ""}
                              label="Barcode"
                              name="barcode"
                              handleChange={handleChange}
                              error={errors.barcode}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds direction="horizontal">
                            <Input
                              inputType="number"
                              value={values.minimumQuantity || ""}
                              label="Minimum Order Quantity"
                              name="minimumQuantity"
                              handleChange={handleChange}
                              error={errors.minimumQuantity}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds direction="horizontal">
                            <Input
                              inputType="number"
                              value={values.weight || ""}
                              label="Product Weight (KG)"
                              name="weight"
                              handleChange={handleChange}
                              error={errors.weight}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds
                            direction="horizontal"
                            style={{ alignItems: "center" }}
                          >
                            <Label
                              for="madeToOrder"
                              label="Made to order"
                            />
                          </Grid.Bounds>

                          <Switch
                            onChange={e => {
                              setFieldValue("madeToOrder", e);
                            }}
                            checked={values.madeToOrder}
                          />
                        </FormRow>

                        {!values.madeToOrder && (
                          <FormRow>
                            <Grid.Bounds
                              direction="horizontal"
                              className="leadTimeGroupId-wrapper"
                            >
                              <Select
                                value={values.leadTimeGroupId}
                                name="leadTimeGroupId"
                                handleChange={handleChange}
                                label="Lead time"
                                options={(globalSettings?.leadTimeGroups || []).map(l => ({
                                  text: l.name.toString(),
                                  value: l.id.toString()
                                }))}
                                error={errors.leadTimeGroupId}
                              />

                              <InputSub
                                width="50"
                                borderright="false"
                              >
                                days
                              </InputSub>
                            </Grid.Bounds>
                          </FormRow>
                        )}

                        <FormRow>
                          <Grid.Bounds
                            direction="horizontal"
                            className="shelf-life-wrapper"
                          >
                            <Input
                              inputType="number"
                              value={values.shelfLife || ""}
                              label={terminology?.shelfLife?.singular?.text || "Shelf life"}
                              name="shelfLife"
                              handleChange={handleChange}
                              error={errors.shelfLife}
                            />

                            <Select
                              className="shelf-life-preiod"
                              label=""
                              forceHideLabel
                              name="shelfLifePeriod"
                              handleChange={handleChange}
                              defaultValue={ShelfLifePeriod.Days}
                              options={$enum(ShelfLifePeriod).getValues().map(slp => ({
                                text: slp,
                                value: slp
                              }))}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Select
                            value={values.supplierId}
                            error={errors.supplierId}
                            name="supplierId"
                            handleChange={handleChange}
                            label={supplierHeaderMapping?.external || "Supplier"}
                            options={(suppliersRes.data || []).map(s => ({
                              text: s.name,
                              value: s.id.toString()
                            }))}
                            unselectedOption={{
                              text: "No supplier associated",
                              value: ""
                            }}
                          />
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds direction="horizontal">
                            <Input
                              disabled={disabled}
                              value={values.supplierPartNumber || ""}
                              label={`Supplier ${terminology.partNumber.singular.text}`}
                              name="supplierPartNumber"
                              handleChange={handleChange}
                              error={errors.supplierPartNumber}
                            />
                          </Grid.Bounds>
                        </FormRow>

                        <FormRow>
                          <Grid.Bounds
                            direction="horizontal"
                            style={{ alignItems: "center" }}
                          >
                            <Label
                              for="creditOnly"
                              label="Allow for crediting only"
                            />
                          </Grid.Bounds>

                          <Switch
                            onChange={e => {
                              setFieldValue("creditOnly", e);
                            }}
                            checked={values.creditOnly}
                          />
                        </FormRow>

                      </FormSection>

                      <FormSection>
                        <h2>
                          Nutritional Image
                        </h2>

                        <FormRow>
                          <Grid.Box style={{ paddingRight: "20px" }}>
                            <InputImage
                              label="Nutrition image"
                              autoHideLabels
                              name="nutritionImage"
                              handleChange={evt => setFieldValue("nutritionImage", evt.currentTarget.files[ 0 ])}
                              value={values.nutritionImage}
                            />
                          </Grid.Box>
                        </FormRow>

                      </FormSection>

                      <FormSection>
                        <h2>
                          Visibility
                        </h2>

                        <Grid.Bounds
                          margin="30px 0"
                          direction="vertical"
                        >
                          <Grid.Box>
                            <FormRow>
                              <Grid.Box style={{ paddingRight: "10px" }}>
                                <Select
                                  value={values.published}
                                  error={errors.published}
                                  name="published"
                                  handleChange={handleChange}
                                  label="Published"
                                  options={published.map(s => ({
                                    text: s,
                                    value: s.toLowerCase()
                                  }))}
                                />
                              </Grid.Box>
                            </FormRow>
                          </Grid.Box>
                        </Grid.Bounds>
                      </FormSection>

                      <FormSection>
                        <h2>
                          Additional Information
                        </h2>

                        <Grid.Bounds
                          margin="30px 0"
                          direction="vertical"
                        >
                          <FormRow>
                            <Grid.Bounds
                              direction="horizontal"
                              style={{ alignItems: "center" }}
                            >
                              <Label
                                for="aboutProductToggle"
                                label={`About this ${terminology.product.singular.text}`}
                              />
                            </Grid.Bounds>

                            <TextArea
                              name="infoKey"
                              label="Additional information"
                              autoHideLabels
                              wysiwyg
                              setFieldValue={setFieldValue}
                              value={values.infoKey}
                            />
                          </FormRow>

                          <FormRow>
                            <Grid.Bounds
                              direction="horizontal"
                              style={{ alignItems: "center" }}
                            >
                              <Label
                                for="ingredients"
                                label="Ingredient list"
                              />
                            </Grid.Bounds>

                            <TextArea
                              name="ingredients"
                              label="Ingredient list"
                              autoHideLabels
                              wysiwyg
                              setFieldValue={setFieldValue}
                              value={values.ingredients}
                            />
                          </FormRow>
                        </Grid.Bounds>
                      </FormSection>
                    </form>
                  );
                }}
              </Formik>

              {product.currentStatus && (
                <>
                  <ProductStatuses
                    type="current"
                    currentStatus={product.currentStatus}
                  />

                  <ProductStatuses
                    type="future"
                    futureStatusesRef={productStatusesRef}
                    currentStatus={product.currentStatus}
                    productStatuses={product.productStatuses}
                  />
                </>
              )}

              {installedModules.includes("product-tag") && !!product.tags && (
                <ProductTagSelector
                  ref={tagRef}
                  productTags={product.tags}
                  productId={product.id}
                />
              )}
            </>
          )
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <ProductInfoSidebar
          terminology={terminology}
          product={product}
          isLoading={isLoading}
          isAdmin={currentRole?.type === "admin"}
          supplierDisplayTerm={supplierHeaderMapping?.external}
          primaryAction={(
            <Button
              $buttonType="primary"
              background="lightGrey"
              onClick={() => history.goBack()}
              disabled={isSavingProduct}
            >
              Cancel
            </Button>
          )}
          secondaryAction={(
            <Button
              loading={isSavingProduct}
              $buttonType="submit"
              type="submit"
              background="primaryDark"
              onClick={handleSaveClick}
            >
              Save
            </Button>
          )}
        />
      </InfoBarWrapper>

      {imgCropperModal && (
        <ImageCroppingModal
          show
          src={imgCropperModal.file}
          onDismiss={handleImgModalOnDismiss}
          onSubmit={handleImgModalOnSubmit}
        />
      )}
    </Layout>
  );
};

const InputSub = styled(Grid.Box)<{ borderright: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  ${({
    theme, width, borderright
  }) => css`
    width: ${width}px;
    background: ${theme.colors.white};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    ${borderright === "true" ? `border-right: 2px solid ${theme.colors.lightGrey}` : `border-left: 2px solid ${theme.colors.lightGrey}`};
    color: ${theme.fonts.colors.primary};

    &.shelf-life-sub {
      align-self: flex-end;
    }
  `}
`;

const FormSection = styled.div`
  .price-wrapper {
    flex-wrap: wrap;
  }
  .leadTimeGroupId-wrapper {
    flex-wrap: wrap;

    div:first-child {
      width: 100%;
    }
  }

  .shelf-life-preiod {
    height: 40px;
    align-self: flex-end;

    select {
      height: 40px;
    }
  }

  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.lightGrey}
    margin: ${theme.spacing.padding.md} 0;
    padding-bottom: ${theme.spacing.padding.md};

    h2 {
      font-size: ${theme.fonts.sizes.h2};
    }
  `};
`;

export default ProductDetailsPage;
